import { CommonModule } from '@angular/common';
import {
   Component,
   Input,
   OnInit,
   ViewChild,
   ViewEncapsulation,
   inject,
   input,
} from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatMenuTrigger } from '@angular/material/menu';
import { Router, RouterModule } from '@angular/router';
import { BaseComponent } from '@core/base/base.component';
import { LocalStorageService } from '@core/services/local-storage.service';
import { environment } from '@env';
import * as signalR from '@microsoft/signalr';
import {
   OrderCenterNotification,
   OrderCenterNotificationConfig,
   OrderCenterNotificationMessage,
} from '@models/order-center-notification.model';
import { QueryModelUpdated } from '@models/query-model-updated.model';
import { TranslateModule } from '@ngx-translate/core';
import { OrderCenterNotificationService } from '@services/order-center-notification.service';
import { TablerIconComponent } from 'angular-tabler-icons';
import moment from 'moment';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { debounceTime, distinctUntilChanged, take } from 'rxjs';
import { ConnectionResolver } from 'src/app/core/api/signalr.connectionresolver';
import { MaterialModule } from 'src/app/core/material.module';
import { AuthService } from 'src/app/core/services/auth.service';
import { NotificationService } from 'src/app/core/services/notification.service';
import { FunctionService } from 'src/app/core/utils/function.service';

@Component({
   selector: 'app-order-center-notification',
   standalone: true,
   imports: [
      CommonModule,
      TablerIconComponent,
      MaterialModule,
      NgScrollbarModule,
      RouterModule,
      TranslateModule,
      FormsModule,
      ReactiveFormsModule,
   ],
   templateUrl: './order-center-notification.component.html',
   styleUrl: './order-center-notification.component.scss',
   providers: [],
   encapsulation: ViewEncapsulation.None,
})
export class OrderCenterNotificationComponent extends BaseComponent implements OnInit {
   // Variables
   showNotification = environment.notification;
   failedRequests = 0;
   failedHubConnectionAttempts = 0;
   connection: signalR.HubConnection | null = null;
   companiesCount = 0;
   selectedConfig!: OrderCenterNotificationConfig;
   orderCenterConfigs: OrderCenterNotificationConfig[];
   orderCenterPausePeriod: number;
   isOpened = false;
   isPaused = false;
   openOrders = 0;
   notificationIntervalMilliseconds = 30000;
   notificationRingIntervalMilliseconds = 20000;
   notificationTokenCooldownMilliseconds = 18000;
   notificationRingCooldownSeconds = 20;
   isPauseRequestLoading = false;
   lastRing: moment.Moment;
   // notificationInterval!: NodeJS.Timer;
   notificationInterval: any;
   notificationRingInterval: any;
   notificationRefreshTimeouts: any[] = [];
   notificationAudio: HTMLAudioElement | null = null;
   audioUnlockListener: (() => void) | null = null;
   pendingNewOrderNotificationRing = false;
   time: number;
   form: FormGroup = new FormGroup({});

   timePauseList: any[] = [
      { Time: 30, Name: 'MinutesLC', Value: 30 },
      { Time: 4, Name: 'HoursLC', Value: 240 },
      { Time: 12, Name: 'HoursLC', Value: 720 },
      { Time: 24, Name: 'HoursLC', Value: 1440 },
   ];

   // Services
   private formBuilder = inject(FormBuilder);
   authService = inject(AuthService);
   notificationService = inject(NotificationService);
   orderCenterNotificationService = inject(OrderCenterNotificationService);
   functionService = inject(FunctionService);
   router = inject(Router);
   localStorageService = inject(LocalStorageService);
   connectionResolver = inject(ConnectionResolver);

   //inputs
   readonly companyId = input<number>();

   _bindedPosId: string;
   @Input()
   get bindedPosId(): string {
      return this._bindedPosId;
   }
   set bindedPosId(val: string) {
      this._bindedPosId = val;
      localStorage.removeItem('erp-notification-token');

      if (val) {
         // this.notificationService.showAlertBox(
         //    'Sua Central de Pedido carregada e pronta para uso!!'
         // );
      }

      setTimeout(() => {
         this.configureNotificationInterval();
      }, 100);
   }

   @ViewChild(MatMenuTrigger) menuTrigger!: MatMenuTrigger;

   // Getters
   get f() {
      return this.form.controls;
   }

   //#region Livecycle hooks
   ngOnInit() {
      this.onSetFormValue();

      if (environment.notification) {
         this.authService.authorizationTokenObservable
            .pipe(this.takeUntilDestroy())
            .subscribe((val) => {
               if (val) {
                  localStorage.removeItem('erp-notification-token');
                  this.configureNotificationInterval();
               }
            });

         this.configureSignalR();
      }
   }

   override ngOnDestroy() {
      super.ngOnDestroy();
      clearInterval(this.notificationInterval);
      this.clearNotificationRefreshRetries();
      this.stopPendingOrdersRing();
   }

   onCloseMenuOrderCenterNotification(): void {
      this.menuTrigger.closeMenu();
   }

   //#region Set Values
   /** Set Form */
   onSetFormValue() {
      /**Form Control */
      this.form = this.formBuilder.group({
         TimePause: [null],
      });

      this.setupValueChangesSubscription();
   }

   //#region  Order Center Notification
   configureNotificationInterval() {
      this.clearNotificationRefreshRetries();
      this.companiesCount = 0;
      this.selectedConfig = null!;
      this.orderCenterConfigs = [];
      this.isOpened = false;
      this.isPaused = false;
      this.openOrders = 0;
      this.stopPendingOrdersRing();

      clearInterval(this.notificationInterval);

      const hasReadPermission = this.authService.hasReadPermission('OrderCenterPanel');
      const bindedPosId = this.localStorageService.getBindedPosId();

      if (hasReadPermission || bindedPosId) {
         this.refreshNotificationFromInitialLoad();
         this.notificationInterval = setInterval(
            () => this.tickNotification(),
            this.notificationIntervalMilliseconds,
         );
      }
   }

   tickNotification(forceUpdate = false) {
      const dateTimestamp = new Date().getTime();
      const lastAccess = Number(localStorage.getItem('erp-notification-token'));

      if (
         forceUpdate ||
         !lastAccess ||
         dateTimestamp > lastAccess + this.notificationTokenCooldownMilliseconds
      ) {
         localStorage.setItem('erp-notification-token', dateTimestamp.toString());

         this.orderCenterNotificationService
            .getNotifications(moment().local().utcOffset())
            .pipe(take(1))
            .subscribe({
               next: (encodedRet) => {
                  if (encodedRet) {
                     const ret: OrderCenterNotification = JSON.parse(
                        this.functionService.decodeString(encodedRet),
                     );

                     this.failedRequests = 0;
                     this.companiesCount = ret.CompaniesCount;
                     this.orderCenterConfigs = ret.OrderCenterConfigs;

                     const previousOpenOrders = this.openOrders;

                     this.isOpened = ret.IsOpened;
                     this.isPaused = ret.IsPaused;
                     this.openOrders = ret.OpenOrders || 0;

                     if (this.openOrders > 0) {
                        const pendingOrdersStarted = previousOpenOrders <= 0;
                        this.configurePendingOrdersRing(pendingOrdersStarted);
                     } else {
                        this.stopPendingOrdersRing();

                        if (this.pendingNewOrderNotificationRing) {
                           this.playNotificationRing(true, false);
                        }
                     }

                     this.pendingNewOrderNotificationRing = false;

                     if (ret.OrderCenterConfigs && ret.OrderCenterConfigs.length === 1) {
                        this.selectedConfig = ret.OrderCenterConfigs[0];
                     }

                     this.toggleSignalRConnection();
                  } else {
                     this.companiesCount = 0;
                     this.selectedConfig = null!;
                     this.isOpened = false;
                     this.isPaused = false;
                     this.openOrders = 0;
                     this.stopPendingOrdersRing();
                  }
               },
               error: (error: any) => {
                  this.failedRequests++;
                  this.companiesCount = 0;
                  this.selectedConfig = null!;
                  this.isOpened = false;
                  this.isPaused = false;
                  this.openOrders = 0;
                  this.stopPendingOrdersRing();

                  if (this.failedRequests >= 3) {
                     clearInterval(this.notificationInterval);
                     this.notificationService.showToast('error', 'NotificationErrorMsg');
                  }
               },
               complete: () => {},
            });
      }
   }

   openOrderCenterDetail(companyId: number) {
      this.selectedConfig = this.orderCenterConfigs.find((n) => n.CompanyId == companyId) || null!;
   }

   returnToCompanyList() {
      this.selectedConfig = null!;
      this.orderCenterPausePeriod = null!;
   }

   tooglePauseStore() {
      let subscription: any = null;
      this.isPauseRequestLoading = true;
      this.notificationService.showToast();

      this.orderCenterNotificationService
         .pauseStore(
            this.selectedConfig.CompanyId,
            this.isPaused ? null! : this.orderCenterPausePeriod,
         )
         .pipe(take(1))
         .subscribe({
            next: (ret) => {},
            error: (err) => {
               subscription?.unsubscribe();
               this.isPauseRequestLoading = false;
               this.notificationService.showExceptionDialogError(err);
            },
         });

      subscription = this.connectionResolver.queryModelUpdated
         .pipe(this.takeUntilDestroy())
         .subscribe((message: QueryModelUpdated) => {
            const commandMessage = this.isPaused
               ? this.orderCenterNotificationService.storeUnpausedMessage
               : this.orderCenterNotificationService.storePausedMessage;
            if (message.Message === commandMessage) {
               subscription.unsubscribe();
               this.orderCenterPausePeriod = null!;

               if (this.orderCenterConfigs && this.orderCenterConfigs.length > 1) {
                  this.selectedConfig = null!;
               }

               this.isPauseRequestLoading = false;
               this.tickNotification(true);
               this.notificationService.showToastSuccess(commandMessage);
            }
         });
   }

   configureSignalR() {
      this.connection = new signalR.HubConnectionBuilder()
         .withUrl(environment.orderCenterHubUrl)
         .build();
      this.connection.serverTimeoutInMilliseconds = 60000;
      this.connection.keepAliveIntervalInMilliseconds = 30000;

      this.connection.on('GetOrderStatusChange', (orderId: string, statusCode: string) => {
         if (orderId && statusCode) {
            this.orderCenterNotificationService.setOrderStatusChanged();
            this.refreshNotificationFromSignalR();
         }
      });

      this.connection.on(
         'GetOrderChangeNotification',
         (clientNotifications: OrderCenterNotificationMessage[]) => {
            if (clientNotifications && clientNotifications.length > 0) {
               this.orderCenterNotificationService.setNotificationCreated();

               if (this.hasNewOrderNotification(clientNotifications)) {
                  this.pendingNewOrderNotificationRing = true;
               }

               this.refreshNotificationFromSignalR();

               clientNotifications.forEach((notification) => {
                  this.notificationService.showToast('info', notification.message);
               });
            }
         },
      );

      console.log('OrderCenter SignalR connection configured.');

      this.connection.onclose((event) => {
         console.log('OrderCenter SignalR connection closed.');

         const isInfiniteAttempts = event?.message?.includes('status code: 1000') || false;

         if (this.canConnectOrderCenterHub()) {
            setTimeout(() => {
               console.log('Reconnecting...');
               this.toggleSignalRConnection(isInfiniteAttempts);
            }, 5000);
         }
      });
   }

   toggleSignalRConnection(isInfiniteAttempts = false) {
      if (
         this.canConnectOrderCenterHub() &&
         (!this.connection || this.connection.state === signalR.HubConnectionState.Disconnected)
      ) {
         this.configureSignalR();

         this.connection
            ?.start()
            .then(() => {
               const companyIds = this.orderCenterConfigs.map((n) => n.CompanyId);
               this.connection?.send('AcknowledgeClient', companyIds);

               this.failedHubConnectionAttempts = 0;
               console.log('OrderCenter SignalR connection estabilished.');
            })
            .catch(() => {
               if (!isInfiniteAttempts) {
                  this.failedHubConnectionAttempts++;
               }

               console.log('Could not connect to OrderCenter SignalR.');

               if (this.failedHubConnectionAttempts < 3) {
                  this.toggleSignalRConnection(isInfiniteAttempts);
               } else {
                  this.notificationService.showToast('error', 'NotificationErrorMsg');
                  clearInterval(this.notificationInterval);
               }
            });
      } else if (
         !this.canConnectOrderCenterHub() &&
         this.connection &&
         this.connection.state === signalR.HubConnectionState.Connected
      ) {
         this.connection
            .stop()
            .then(() => {})
            .catch(() => {
               console.log('Could not stop OrderCenter SignalR connection.');
            });
      }
   }

   private canConnectOrderCenterHub() {
      return this.orderCenterConfigs && this.orderCenterConfigs.length > 0;
   }

   private refreshNotificationFromSignalR() {
      this.clearNotificationRefreshRetries();
      this.tickNotification(true);

      [2000].forEach((delay) => {
         const timeout = setTimeout(() => {
            this.notificationRefreshTimeouts = this.notificationRefreshTimeouts.filter(
               (item) => item !== timeout,
            );

            if (this.openOrders <= 0) {
               this.tickNotification(true);
            }
         }, delay);

         this.notificationRefreshTimeouts.push(timeout);
      });
   }

   private refreshNotificationFromInitialLoad() {
      this.clearNotificationRefreshRetries();
      this.tickNotification(true);

      [2000].forEach((delay) => {
         const timeout = setTimeout(() => {
            this.notificationRefreshTimeouts = this.notificationRefreshTimeouts.filter(
               (item) => item !== timeout,
            );

            if (this.openOrders <= 0 || !this.notificationRingInterval) {
               this.tickNotification(true);
            }
         }, delay);

         this.notificationRefreshTimeouts.push(timeout);
      });
   }

   private clearNotificationRefreshRetries() {
      this.notificationRefreshTimeouts.forEach((timeout) => clearTimeout(timeout));
      this.notificationRefreshTimeouts = [];
   }

   playNotificationRing(bypassCooldown = false, requireOpenOrders = true) {
      const currentDate = moment();
      const secondsSinceLastRing = this.lastRing
         ? currentDate.diff(this.lastRing, 'seconds', true)
         : null;

      if (
         bypassCooldown ||
         secondsSinceLastRing === null ||
         secondsSinceLastRing >= this.notificationRingCooldownSeconds
      ) {
         this.lastRing = currentDate.clone();
         this.stopNotificationRing();

         const audio = new Audio();
         audio.src = './assets/media/' + environment.orderCenterRing;
         this.notificationAudio = audio;

         audio.addEventListener(
            'canplaythrough',
            () => {
               this.playNotificationAudio(audio, requireOpenOrders);
            },
            { once: true },
         );

         audio.load();
         this.playNotificationAudio(audio, requireOpenOrders);
      }
   }

   configurePendingOrdersRing(playImmediately = false) {
      if (this.openOrders <= 0) {
         this.stopPendingOrdersRing();
         return;
      }

      if (playImmediately || !this.notificationRingInterval) {
         this.playNotificationRing(true);
         this.restartNotificationRingInterval();
      }
   }

   restartNotificationRingInterval() {
      clearInterval(this.notificationRingInterval);

      this.notificationRingInterval = setInterval(() => {
         if (this.openOrders > 0) {
            this.playNotificationRing(true);
         } else {
            this.stopPendingOrdersRing();
         }
      }, this.notificationRingIntervalMilliseconds);
   }

   stopPendingOrdersRing() {
      clearInterval(this.notificationRingInterval);
      this.notificationRingInterval = null;
      this.stopNotificationRing();
   }

   stopNotificationRing() {
      this.clearAudioUnlockListener();

      if (!this.notificationAudio) {
         return;
      }

      try {
         this.notificationAudio.pause();
         this.notificationAudio.currentTime = 0;
      } catch (error) {}

      this.notificationAudio = null;
   }

   private playNotificationAudio(audio: HTMLAudioElement, requireOpenOrders = true) {
      if (this.notificationAudio !== audio || (requireOpenOrders && this.openOrders <= 0)) {
         return;
      }

      if (!audio.paused && !audio.ended) {
         return;
      }

      try {
         const playPromise = audio.play();

         playPromise?.catch(() => {
            this.configureAudioUnlock(audio, requireOpenOrders);
         });
      } catch (error) {
         this.configureAudioUnlock(audio, requireOpenOrders);
      }
   }

   private configureAudioUnlock(audio: HTMLAudioElement, requireOpenOrders = true) {
      if (this.audioUnlockListener) {
         return;
      }

      this.audioUnlockListener = () => {
         this.clearAudioUnlockListener();
         this.playNotificationAudio(audio, requireOpenOrders);
      };

      window.addEventListener('click', this.audioUnlockListener);
      window.addEventListener('keydown', this.audioUnlockListener);
   }

   private clearAudioUnlockListener() {
      if (!this.audioUnlockListener) {
         return;
      }

      window.removeEventListener('click', this.audioUnlockListener);
      window.removeEventListener('keydown', this.audioUnlockListener);
      this.audioUnlockListener = null;
   }

   private hasNewOrderNotification(notifications: OrderCenterNotificationMessage[]) {
      return notifications.some((notification) =>
         this.isNewOrderNotification(notification.message),
      );
   }

   private isNewOrderNotification(message: string) {
      const normalizedMessage = message?.toUpperCase() || '';

      return (
         (normalizedMessage.includes('ORDERPLACED') &&
            !normalizedMessage.includes('DISKDELIVERY')) ||
         message === 'FoodIntegrationIfoodOrderAccepted'
      );
   }

   onRedirectStore() {
      this.onCloseMenuOrderCenterNotification();
      this.router.navigate([`store/${this.companyId()}/delivery`], { state: { tab: 1 } });
   }

   //#region Configura a subscrição a valueChanges */
   private setupValueChangesSubscription() {
      // IntegrationFeeType
      this.f['TimePause'].valueChanges
         .pipe(debounceTime(300), distinctUntilChanged(), this.takeUntilDestroy())
         .subscribe((timePause) => {
            this.orderCenterPausePeriod = timePause;
         });
   }
}
